import { useTranslation } from "react-i18next";
import { IconCheckCircle } from "../../../../../common/icons/IconCheckCircle/IconCheckCircle";
import defaultContent from "../../../../../content/leadershipSeminar";
import type { PathRequirementProps } from "../types";
import "./PathRequirement.scss";

export const PathRequirement = ({ contentKey, contentParams, status }: PathRequirementProps) => {
  const { t } = useTranslation(["leadershipSeminar"]);

  const getDefaultValue = (key: string): string => {
    const parts = key.split(".");
    let current: unknown = defaultContent;
    for (const part of parts) {
      if (current && typeof current === "object" && part in current) {
        current = (current as Record<string, unknown>)[part];
      } else {
        return key;
      }
    }
    return typeof current === "string" ? current : key;
  };

  const getTranslatedLabel = (): string => {
    const defaultValue = getDefaultValue(contentKey);
    // Pass contentParams and defaultValue as options object for i18next interpolation
    return t(contentKey, { defaultValue, ...contentParams });
  };

  const label = getTranslatedLabel();
  const isEitherOr = status.length > 1;
  const anyMet = status.some((s) => s);

  const getAccessibleDescription = (): string => {
    if (isEitherOr) {
      const metCount = status.filter((s) => s).length;
      const eitherOrText = String(
        t("pathView.eitherOrRequirement", defaultContent.pathView.eitherOrRequirement, {
          metCount,
          totalCount: status.length,
        }),
      );
      return `${label}. ${eitherOrText}`;
    }
    const statusText = status[0]
      ? String(t("pathView.requirementMet", defaultContent.pathView.requirementMet))
      : String(t("pathView.requirementNotMet", defaultContent.pathView.requirementNotMet));
    return `${label}. ${statusText}`;
  };

  const getScreenReaderText = (): string => {
    if (isEitherOr) {
      return anyMet
        ? String(t("pathView.eitherOrConditionMet", defaultContent.pathView.eitherOrConditionMet))
        : String(t("pathView.eitherOrConditionNotMet", defaultContent.pathView.eitherOrConditionNotMet));
    }
    return status[0]
      ? String(t("pathView.requirementMet", defaultContent.pathView.requirementMet))
      : String(t("pathView.requirementNotMet", defaultContent.pathView.requirementNotMet));
  };

  return (
    <div className="path-requirement" role="listitem" aria-label={getAccessibleDescription()}>
      <div className="path-requirement__checkmarks">
        {status.map((isMet, index) => (
          <div key={`${contentKey}-${index}`} className="path-requirement__check-wrapper">
            <IconCheckCircle
              color={isMet ? "green" : "gray"}
              size="lg"
              id={`path-req-check-${contentKey}-${index}`}
              solidCheckCircle={false}
            />
            {isEitherOr && index < status.length - 1 && (
              <div className="path-requirement__divider" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>
      <span className="path-requirement__label">{label}</span>
      <span className="sr-only">{getScreenReaderText()}</span>
    </div>
  );
};

export default PathRequirement;
